import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { BRANDS, type BrandKey, type BrandTheme } from './brand.config';
import { resolveBrandFromUrl } from './brand.resolve';

type BrandContextValue = BrandTheme;

const BrandContext = createContext<BrandContextValue>(BRANDS.propfit);

type BrandProviderProps = {
  children: React.ReactNode;
  forceHost?: string;
  defaultKey?: BrandKey;
};

export const BrandProvider = ({
  children,
  forceHost,
  defaultKey = 'propfit',
}: BrandProviderProps) => {
  const theme = useMemo(() => {
    const host =
      forceHost ??
      (typeof window !== 'undefined' ? window.location.hostname : undefined);
    const key = host
      ? resolveBrandFromUrl(`https://${host}`, defaultKey)
      : defaultKey;
    return BRANDS[key];
  }, [forceHost, defaultKey]);

  // Set document title based on brand
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = theme.name;
    }
  }, [theme.name]);

  return (
    <BrandContext.Provider value={theme}>{children}</BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);

export const useBrandName = () => {
  const brand = useBrand();
  return brand.name;
};

export const BrandLogo = (props: React.SVGProps<SVGSVGElement>) => {
  const brand = useBrand();
  const Logo = brand.logo;
  return <Logo {...props} />;
};

export const BrandAuthLogo = (props: React.SVGProps<SVGSVGElement>) => {
  const brand = useBrand();
  const Logo = brand.authLogo ?? brand.logo;
  return <Logo {...props} />;
};

export const BrandSquare = (props: React.SVGProps<SVGSVGElement>) => {
  const brand = useBrand();
  const Logo = brand.square ?? brand.logo;
  return <Logo {...props} />;
};

export const BrandSquareMono = (props: React.SVGProps<SVGSVGElement>) => {
  const brand = useBrand();
  const Logo = brand.squareMono ?? brand.square ?? brand.logo;
  return <Logo {...props} />;
};
